import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchPolitician } from "@/lib/api";
import ArticleCard from "@/components/ArticleCard";
import Seo from "@/components/Seo";
import { ChevronLeft, TrendingDown, TrendingUp, Award, AlertTriangle, Clock } from "lucide-react";

export default function PoliticianDetail() {
    const { id } = useParams();
    const [politician, setPolitician] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        setPolitician(null);
        setErr(null);
        fetchPolitician(id)
            .then(setPolitician)
            .catch(() => setErr("Politiker ikke fundet"));
    }, [id]);

    if (err)
        return (
            <div className="max-w-7xl mx-auto px-4 py-20 text-center" data-testid="politician-error">
                <p className="text-[#BB1B2D] text-lg font-semibold">{err}</p>
                <Link to="/politikere" className="text-royal underline mt-4 inline-block">
                    Tilbage til politikere
                </Link>
            </div>
        );
    if (!politician)
        return (
            <div className="max-w-7xl mx-auto px-4 py-20 text-center text-[#4F5A7A]">Indlæser...</div>
        );

    const initials = politician.name
        .split(" ")
        .map((n) => n[0])
        .slice(0, 2)
        .join("");
    const isCritical = politician.mentions_count > 0 && politician.truth_score < 50;
    const isGood = politician.mentions_count === 0 || politician.truth_score >= 80;
    const scoreColor = isCritical ? "#BB1B2D" : isGood ? "#186A3B" : "#C78300";

    return (
        <div data-testid="politician-detail">
            <Seo
                title={`${politician.name} — ${politician.role}, ${politician.party_name}`}
                description={`Sandhedsscore ${politician.truth_score}% for ${politician.name} (${politician.role} for ${politician.party_name}). Baseret på ${politician.mentions_count} omtaler, ${politician.lies_count} dokumenterede løgne og ${politician.valgflaesk_count} valgflæsk-tilfælde.`}
                keywords={`${politician.name}, ${politician.party_name}, ${politician.role}, Folketingsmedlem, sandhedsscore, faktatjek, valgløfter`}
                path={`/politikere/${id}`}
                type="profile"
                structuredData={{
                    "@context": "https://schema.org",
                    "@type": "Person",
                    "name": politician.name,
                    "jobTitle": politician.role,
                    "memberOf": {
                        "@type": "PoliticalParty",
                        "name": politician.party_name,
                    },
                    "nationality": "Danish",
                }}
            />
            {/* HERO */}
            <section
                className="text-white relative overflow-hidden grain"
                style={{
                    background: `linear-gradient(135deg, ${politician.party_color} 0%, #061845 100%)`,
                }}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                    <Link
                        to="/politikere"
                        className="inline-flex items-center gap-1 text-white/70 hover:text-white text-sm mb-6"
                        data-testid="back-to-politicians"
                    >
                        <ChevronLeft className="w-4 h-4" /> Tilbage til politikere
                    </Link>

                    <div className="grid md:grid-cols-12 gap-10 items-start">
                        <div className="md:col-span-8">
                            <div className="flex items-center gap-5 mb-5">
                                <div
                                    className="w-24 h-24 rounded-full bg-white flex items-center justify-center font-serif-dm text-4xl font-bold"
                                    style={{ color: politician.party_color }}
                                >
                                    {initials}
                                </div>
                                <div>
                                    <h1 className="font-serif-dm text-4xl sm:text-5xl leading-tight">
                                        {politician.name}
                                    </h1>
                                    <p className="text-white/80 mt-1 text-lg">
                                        {politician.role} ·{" "}
                                        <Link
                                            to={`/partier/${politician.party_id}`}
                                            className="underline hover:text-white"
                                        >
                                            {politician.party_name} ({politician.party_short_name})
                                        </Link>
                                    </p>
                                </div>
                            </div>
                            {politician.last_updated && (
                                <p className="text-white/60 text-sm inline-flex items-center gap-1.5">
                                    <Clock className="w-3.5 h-3.5" />
                                    Senest opdateret:{" "}
                                    {new Date(politician.last_updated).toLocaleDateString("da-DK", {
                                        day: "2-digit",
                                        month: "long",
                                        year: "numeric",
                                    })}
                                </p>
                            )}
                        </div>

                        <div className="md:col-span-4">
                            <div className="bg-white rounded-2xl p-6 text-[#0B1A3A]">
                                <div className="text-xs uppercase tracking-[0.2em] text-[#4F5A7A] font-semibold mb-2">
                                    Sandhedsscore
                                </div>
                                <div className="flex items-baseline gap-2 mb-3">
                                    <span
                                        className="font-serif-dm text-6xl"
                                        style={{ color: scoreColor }}
                                    >
                                        {politician.truth_score}%
                                    </span>
                                    {isCritical && <TrendingDown className="w-7 h-7 text-[#BB1B2D]" />}
                                    {isGood && politician.mentions_count > 0 && (
                                        <Award className="w-7 h-7 text-[#186A3B]" />
                                    )}
                                </div>
                                <div className="h-2 rounded-full bg-[#EEF0F7] overflow-hidden mb-5">
                                    <div
                                        className="h-full rounded-full transition-all"
                                        style={{
                                            width: `${politician.truth_score}%`,
                                            background: scoreColor,
                                        }}
                                    />
                                </div>
                                <div className="grid grid-cols-3 gap-3 text-center pt-4 border-t border-[#EEF0F7]">
                                    <div>
                                        <div className="font-serif-dm text-2xl text-royal">
                                            {politician.mentions_count}
                                        </div>
                                        <div className="text-[10px] uppercase tracking-wider text-[#4F5A7A]">
                                            Omtaler
                                        </div>
                                    </div>
                                    <div>
                                        <div className="font-serif-dm text-2xl text-[#BB1B2D]">
                                            {politician.lies_count}
                                        </div>
                                        <div className="text-[10px] uppercase tracking-wider text-[#4F5A7A]">
                                            Løgne
                                        </div>
                                    </div>
                                    <div>
                                        <div className="font-serif-dm text-2xl text-[#C78300]">
                                            {politician.valgflaesk_count}
                                        </div>
                                        <div className="text-[10px] uppercase tracking-wider text-[#4F5A7A]">
                                            Valgflæsk
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ARTICLES */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
                <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-5 h-5 text-royal" />
                    <span className="text-xs uppercase tracking-[0.2em] text-[#4F5A7A] font-semibold">
                        Dokumentation
                    </span>
                </div>
                <h2 className="font-serif-dm text-3xl sm:text-4xl text-royal mb-8">
                    Artikler hvor {politician.name.split(" ")[0]} nævnes
                </h2>
                {politician.related_articles && politician.related_articles.length > 0 ? (
                    <div className="grid md:grid-cols-2 gap-4">
                        {politician.related_articles.map((a) => (
                            <ArticleCard key={a.id} article={a} partyName={politician.party_name} />
                        ))}
                    </div>
                ) : (
                    <p className="text-[#4F5A7A]">Ingen artikler nævner denne politiker endnu.</p>
                )}
            </section>
        </div>
    );
}
